import React from "react";
import PaneRow from "../../models/PaneRow";
import {
  loadMappings,
  getMappings,
  getCharacterSet,
} from "../../services/MappingService";
import GridColumn, {
  GridColumnConfigProperties,
} from "../../mustangui/Columns/GridColumn";
import { TableChildProps } from "../Table";

interface FilterContextValue {
  filterValue: string | null;
  setFilterValue: (value: string | null) => void;
}

export const FilterContext = React.createContext<FilterContextValue>({
  filterValue: null,
  setFilterValue: () => {},
});

export const FilterProvider: React.FC<{
  children: React.ReactNode;
  filterValue: string | null;
  onFilterChange: (value: string | null) => void;
}> = (props) => (
  <FilterContext.Provider
    value={{
      filterValue: props.filterValue,
      setFilterValue: props.onFilterChange,
    }}
  >
    {props.children}
  </FilterContext.Provider>
);

export function getFilterWords(filterValue: string | null): string[] {
  if (filterValue === null) {
    return [];
  }

  return standardizeFilterValue(filterValue)
    .split(" ")
    .filter((v) => v.length > 0);
}

export async function initializeFilter(): Promise<void> {
  await loadMappings();
}

export function rowMatchesFilter(
  row: PaneRow,
  filterWords: string[],
  columns: GridColumnConfigProperties[],
  propagated: TableChildProps
): boolean {
  const foundWords = new Map<string, boolean>();
  for (const filterWord of filterWords) {
    foundWords.set(filterWord, false);
  }

  for (const column of columns) {
    const filterText = GridColumn.getFilterText(
      column,
      propagated,
      row
    ).toLowerCase();

    for (const filterWord of filterWords) {
      if (filterText.includes(filterWord)) {
        foundWords.set(filterWord, true);
      }
    }
  }

  for (const foundWord of foundWords.values()) {
    if (!foundWord) {
      return false;
    }
  }

  return true;
}

export function standardizeFilterValue(value: string): string {
  const map = getMappings();
  const characterSet = getCharacterSet();
  var chars;

  // This is necessary for certain accents to get split properly for standardization in UTF8
  // It will also enable emoji standardization
  if (characterSet === "AL32UTF8") {
    const decomposed = value.normalize("NFKD").toLowerCase();
    chars = Array.from(decomposed);
  } else {
    chars = Array.from(value.toLowerCase());
  }

  const standardized = chars
    .map((ch) => (map[ch] !== undefined ? map[ch] : ch))
    .join("");

  return standardized;
}
